"use client"

// Drop-in for react-google-recaptcha-v3's useGoogleReCaptcha that loads Google's script only when a
// form is actually submitted (first executeRecaptcha call), instead of ~350 KB on every page load.
type Grecaptcha = {
  ready: (cb: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""
let loading: Promise<Grecaptcha> | null = null

function loadRecaptcha(): Promise<Grecaptcha> {
  if (loading) return loading
  loading = new Promise<Grecaptcha>((resolve, reject) => {
    const w = window as unknown as { grecaptcha?: Grecaptcha }
    if (w.grecaptcha) return w.grecaptcha.ready(() => resolve(w.grecaptcha!))
    const script = document.createElement("script")
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`
    script.async = true
    script.defer = true
    script.onload = () => w.grecaptcha ? w.grecaptcha.ready(() => resolve(w.grecaptcha!)) : reject(new Error("reCAPTCHA unavailable"))
    script.onerror = () => reject(new Error("reCAPTCHA failed to load"))
    document.body.appendChild(script)
  }).catch((error) => {
    loading = null // allow a retry on the next submit
    throw error
  })
  return loading
}

/** Returns a reCAPTCHA v3 token, or "" when no site key is set or Google's script cannot load. */
async function executeRecaptcha(action: string): Promise<string> {
  if (!SITE_KEY || typeof window === "undefined") return ""
  try {
    const grecaptcha = await loadRecaptcha()
    return await grecaptcha.execute(SITE_KEY, { action })
  } catch (error) {
    console.warn("[reCAPTCHA]", error)
    return ""
  }
}

export function useGoogleReCaptcha() {
  return { executeRecaptcha }
}
